import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env';
import { _error, _isFeDev, _log, _warn } from '@shared/aux_helper_environment';
import { _cloneDeep } from '@shared/aux_helper_functions';
import { IanTranslateService, PrismaDynamicEnv } from 'core/services/ian-core-singleton.service';
import { BehaviorSubject, MonoTypeOperatorFunction, Observable, of, throwError, timer } from 'rxjs';
import { catchError, concatMap, finalize, map, retryWhen, switchMap, take, tap, timeout } from 'rxjs/operators';
import {
  GENIE_RETRY_MAX_TOTAL_WAIT_MS,
  GenieErrorType,
  TRANSIENT_RETRY_MAX_TOTAL_WAIT_MS,
  isWarehouseWarmUpError,
  retryTransientDatabricksErrors,
} from './databricks-transient-retry';
import {
  DatabricksStatementExecutionRequest,
  DatabricksStatementExecutionResponse,
  GenieConversationResponse,
  GenieMessage,
  GenieStatementResponse,
} from './databricks-genie-ai-chat.models';

// Límites de seguridad para Statement Execution API
const MAX_SQL_QUERY_LENGTH = 500000; // 500KB max SQL query
const MAX_WAREHOUSE_ID_LENGTH = 100;
const MAX_STATEMENT_ID_LENGTH = 100;
const STATEMENT_EXECUTION_TIMEOUT_MS = 120000; // 2 minutos timeout total
const MAX_POLLING_ATTEMPTS = 60; // Máximo 60 intentos de polling
const MAX_CONSECUTIVE_ERRORS = 5; // Circuit breaker después de 5 errores consecutivos
const CIRCUIT_BREAKER_RESET_MS = 60000; // Reset circuit breaker después de 1 minuto

// La política de reintentos transitorios (warm-up del warehouse, concurrencia Delta) vive en
// databricks-transient-retry.ts. Se re-exporta GenieErrorType para no romper a quien lo importa
// históricamente desde este archivo.
export { GenieErrorType } from './databricks-transient-retry';

// Interfaces para el middleware proxy
export interface GenieProxyRequest {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  relativePath: string;
  body?: any;
  variables?: { [key: string]: string };
}

export interface GenieProxyResponse<T = any> {
  success: boolean;
  statusCode: number;
  content: T;
  errorMessage: string | null;
  metadata: {
    responseTimeMs: number;
    requestTimestamp: string;
  };
}

/*
Documentación del middleware GenieProxy:
Endpoint: POST /api/genieproxy/proxy
Health: GET /api/genieproxy/health

El middleware maneja automáticamente:
- Autenticación con Databricks
- Construcción de URLs completas
- Reemplazo automático de {space_id}
- Manejo de errores y reintentos
*/

const _keyVer = environment.localStorageKeyVerPrefix || '_v3_';

export interface DataBricksAiGenieChatResponse {
  text?: string;
  queryProcessResult?: GenieStatementResponse;
  rawResponse?: GenieMessage;
  error?: GenieErrorType;
  // Propiedades HTTP del error (cuando error está presente)
  originalError?: any;
  status?: number;
  statusText?: string;
  url?: string;
  method?: string; // Método HTTP del request (POST, GET, etc)
  relativePath?: string; // Path relativo del endpoint de Genie
  serverResponse?: any; // Response completo del servidor (para debugging)
}

export interface GenieError {
  type: GenieErrorType;
  message: string;
  originalError?: any;
}

@Injectable({
  providedIn: 'root',
})
export class DataBricksAiGenieChatService {
  private readonly PROXY_ENDPOINT = '/api/datascience/genieproxy/proxy';
  private readonly HEALTH_ENDPOINT = '/api/datascience/genieproxy/health';

  // Host remoto para desarrollo local
  private readonly REMOTE_HOST_LAB = 'https://lab.prismaretail.dev';

  private lasConversationResponseSubject = new BehaviorSubject<GenieConversationResponse | null>(null);
  private rateLimitTimeout?: number;

  // Circuit breaker para polling resiliente
  private consecutivePollingErrors = 0;
  private circuitBreakerOpenUntil = 0;

  /**
   * Obtiene la URL base correcta dependiendo del entorno
   */
  private getBaseUrl(): string {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isLocalhost ? this.REMOTE_HOST_LAB : '';
  }

  /**
   * Construye la URL completa para un endpoint
   */
  private buildUrl(endpoint: string): string {
    return `${this.getBaseUrl()}${endpoint}`;
  }

  private getConfig() {
    const config = this.prismaDynamicEnv.getConf('brandCustomization.dataBricksGenieConfig') || {};

    // Valores por defecto para configuraciones del polling y reintentos
    return {
      ENABLED: false,
      MAX_ATTEMPTS: 30,
      POLLING_FREQUENCY: 2000,
      TIMEOUT: 30000,
      RETRY_DELAY: 2000,
      RETRY_ATTEMPTS: 3,
      RATE_LIMIT_RESET: 60000,
      ...config,
    };
  }

  public CAN_USE_DB_CHAT() {
    // Ahora solo necesitamos que esté habilitado, las credenciales las maneja el backend
    return !!(this.getConfig()?.ENABLED === true);
  }

  /**
   * Obtiene el catalog para Dashboard Storage (executeStatementDirect)
   * Método abstracto para facilitar migración futura si cambia la ubicación
   */
  public getDashboardCatalog(): string {
    return this.getConfig()?.dashBoardCatalog || 'prd_gold_cs';
  }

  /**
   * Obtiene el schema para Dashboard Storage (executeStatementDirect)
   * Método abstracto para facilitar migración futura si cambia la ubicación
   */
  public getDashboardSchema(): string {
    return this.getConfig()?.dashBoardSchema || 'prismart';
  }

  /**
   * Obtiene el catalog para Genie Queries (Genie API)
   * Método abstracto para facilitar migración futura si cambia la ubicación
   */
  public getGenieCatalog(): string {
    return this.getConfig()?.genieCatalog || this.getDashboardCatalog();
  }

  /**
   * Obtiene el schema para Genie Queries (Genie API)
   * Método abstracto para facilitar migración futura si cambia la ubicación
   */
  public getGenieSchema(): string {
    return this.getConfig()?.genieSchema || this.getDashboardSchema();
  }

  /**
   * Obtiene la configuración completa específica de Genie (catalog, schema, threshold)
   * Utiliza los métodos abstractos para obtener catalog y schema
   */
  public getGenieConfig() {
    const config = this.getConfig();
    return {
      catalog: this.getGenieCatalog(),
      schema: this.getGenieSchema(),
      warehouseId: config.warehouse_id || '',
      fullDownloadRowThreshold: config.fullDownloadRowThreshold || 900,
      shouldShowFullDownloadButton: config.shouldShowFullDownloadButton !== false, // Default true si no está definido
      useExternalLinksForCSV: config.useExternalLinksForCSV === true, // Default false si no está definido
      maxPinRowThreshold: config.maxPinRowThreshold || 1000,
    };
  }

  constructor(private http: HttpClient, private prismaDynamicEnv: PrismaDynamicEnv, private translate: IanTranslateService) {
    this.init();
    if (_isFeDev()) {
      _log(['LakehouseAiGenieChatService'], {
        config: this.getConfig(),
        canUseChat: this.CAN_USE_DB_CHAT(),
        baseUrl: this.getBaseUrl(),
        proxyUrl: this.buildUrl(this.PROXY_ENDPOINT),
        healthUrl: this.buildUrl(this.HEALTH_ENDPOINT),
      });
    }
  }

  init() {
    // Hacer health check automático en localhost
    if (false && _isFeDev()) this.performLocalhostHealthCheck();

    if (!this.CAN_USE_DB_CHAT()) return;

    if (false && _isFeDev()) this.testConversation();
  }

  /** Realiza un health check automático solo en localhost */
  private performLocalhostHealthCheck(): void {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (isLocalhost) {
      _log('[LakehouseProxy Health Check] Iniciando verificación automática en localhost...');

      this.getServiceHealth().subscribe({
        next: response => {
          _log('[LakehouseProxy Health Check] ✅ Servicio disponible:', response);
        },
        error: error => {
          _error('[LakehouseProxy Health Check] ❌ Error al conectar con el servicio:', error);
          _error('[LakehouseProxy Health Check] Verificar que el backend esté ejecutándose en:', this.REMOTE_HOST_LAB);
        },
      });
    }
  }

  private testConversation() {
    if (true) {
      this.sendGenieMessage('Quiero un listado en forma de tabla del ranking de los 3 productos con más venta histórica').subscribe(
        data => {
          _log(1, '[chat]', data);
          this.sendGenieMessage('perfecto, puedes hacer un gráfico de torta con esos mismos datos?').subscribe(data => {
            _log(2, '[chat]', data);
          });
        }
      );
    }
  }

  /** Verifica el estado del servicio GenieProxy */
  private checkHealth(): Observable<any> {
    const url = this.buildUrl(this.HEALTH_ENDPOINT);
    return this.http.get(url).pipe(
      tap(response => _log('LakehouseProxy Health:', response)),
      catchError(error => {
        _error('LakehouseProxy Health Check Failed:', error);
        return throwError(() => error);
      })
    );
  }

  /** Realiza una petición al middleware GenieProxy */
  private callGenieProxy<T = any>(request: GenieProxyRequest): Observable<GenieProxyResponse<T>> {
    if (!this.CAN_USE_DB_CHAT()) {
      return throwError(() => ({ type: GenieErrorType.DISABLED, message: 'Genie chat is disabled' }));
    }

    const url = this.buildUrl(this.PROXY_ENDPOINT);
    return this.http.post<GenieProxyResponse<T>>(url, request).pipe(
      tap(response => {
        if (_isFeDev() && false) {
          _log('LakehouseProxy Request:', request);
          _log('LakehouseProxy Response:', response);
          _log('LakehouseProxy URL:', url);
        }
      }),
      map(response => {
        if (!response.success) {
          throw new Error(response.errorMessage || 'GenieProxy request failed');
        }
        return response;
      }),
      catchError(error => this.genericHandleError(error, request))
    );
  }

  /** Simplifica mensajes de error complejos de Databricks para hacerlos más amigables */
  private simplifyDatabricksError(errorMessage: string): string {
    // Validación estricta: si no hay mensaje o no es string, retornar vacío
    if (!errorMessage || typeof errorMessage !== 'string') return '';

    // Intentar parsear JSON embebido en el mensaje
    const jsonMatch = errorMessage.match(/Error content: ({.*})/);
    if (jsonMatch) {
      try {
        const errorData = JSON.parse(jsonMatch[1]);

        // Extraer error_code y message principal
        const errorCode = errorData.error_code || '';
        const message = errorData.message || '';

        // Simplificar mensajes comunes de Databricks
        if (errorCode === 'RESOURCE_DOES_NOT_EXIST') {
          if (message.includes('Unable to get space')) {
            return this.translate.instant('AI_CHAT.ERRORS.DATABRICKS_RESOURCE_NOT_FOUND');
          }
          return this.translate.instant('AI_CHAT.ERRORS.DATABRICKS_RESOURCE_GENERIC');
        }

        if (errorCode === 'PERMISSION_DENIED') {
          return this.translate.instant('AI_CHAT.ERRORS.DATABRICKS_PERMISSION_DENIED');
        }

        if (errorCode === 'INVALID_PARAMETER_VALUE') {
          return this.translate.instant('AI_CHAT.ERRORS.DATABRICKS_INVALID_PARAMS');
        }

        // Si hay un message limpio, usarlo
        if (message && !message.includes('Caused by')) {
          return message.split('Caused by')[0].trim();
        }

        // Retornar solo el message principal sin detalles técnicos
        return message || errorMessage;
      } catch (e) {
        // Si falla el parseo, continuar con simplificación por texto
      }
    }

    // Simplificar por patrones de texto comunes
    if (errorMessage.includes('Response status code does not indicate success')) {
      const statusMatch = errorMessage.match(/: (\w+) \(/);
      const status = statusMatch ? statusMatch[1] : 'Error';

      if (errorMessage.includes('RESOURCE_DOES_NOT_EXIST')) {
        return this.translate.instant('AI_CHAT.ERRORS.DATABRICKS_RESOURCE_GENERIC');
      }

      return `${this.translate.instant('AI_CHAT.ERRORS.DATABRICKS_COMM_ERROR')}: ${status}`;
    }

    // Acortar mensajes muy largos (con validación de length)
    if (errorMessage && errorMessage.length > 200) {
      return errorMessage.substring(0, 200) + '... (ver detalles completos en el error)';
    }

    return errorMessage;
  }

  /**
   * Envuelve la política de reintentos (databricks-transient-retry.ts) con el circuit breaker y el
   * logging del servicio. La lógica vive afuera para poder testearla sin levantar Angular.
   */
  private retryTransientDatabricksErrors<T>(
    label: string,
    maxTotalWaitMs: number = TRANSIENT_RETRY_MAX_TOTAL_WAIT_MS,
    /** Solo para GETs: un 502/504 no garantiza que la operación no se haya ejecutado. */
    retryGatewayErrors = false
  ): MonoTypeOperatorFunction<T> {
    const motivo: Record<string, string> = {
      'warm-up': 'Warehouse levantando (warm-up)',
      'delta-concurrency': 'Concurrencia Delta',
      gateway: 'Error de gateway del proxy',
    };

    return retryTransientDatabricksErrors<T>({
      label,
      maxTotalWaitMs,
      retryGatewayErrors,
      isCircuitBreakerOpen: () => this.circuitBreakerOpenUntil > Date.now(),
      onRetry: ({ reason, delayMs, elapsedMs }) => _warn(`[${label}] ${motivo[reason]}, reintento en ${delayMs}ms`, { elapsedMs }),
      onGiveUp: ({ reason }) => _error(`[${label}] Sin más reintentos: ${reason}`),
    });
  }

  /** Maneja errores HTTP y los transforma en errores observables */
  private genericHandleError(error: HttpErrorResponse, request?: GenieProxyRequest): Observable<never> {
    let genieError: GenieError = {
      type: GenieErrorType.UNKNOWN,
      message: 'An unknown error occurred',
      originalError: error,
    };

    // Intentar extraer mensaje del backend (GenieProxy devuelve { errorMessage })
    let serverMessage = '';
    if (error.error?.errorMessage) {
      serverMessage = error.error.errorMessage;
    } else if (error.error?.message) {
      serverMessage = error.error.message;
    } else if (typeof error.error === 'string') {
      serverMessage = error.error;
    } else if (!(error instanceof HttpErrorResponse) && typeof (error as any)?.message === 'string') {
      // callGenieProxy lanza un Error plano cuando el proxy responde 200 con success:false. Sin este
      // fallback se perdía el mensaje real de Databricks (y con él la señal de warm-up), que quedaba
      // reemplazado por el 'An unknown error occurred' de abajo. Se excluye HttpErrorResponse a
      // propósito: ahí .message es el texto genérico de Angular y pisaría los mensajes traducidos.
      serverMessage = (error as any).message;
    }

    // Parsear y simplificar mensajes de error de Databricks
    serverMessage = this.simplifyDatabricksError(serverMessage);

    // Warehouse levantando: se chequea primero para no quedar tapado por la rama genérica de 503, y
    // para que el usuario lea "esperá unos segundos" en vez de "el servicio no está disponible".
    if (isWarehouseWarmUpError(error)) {
      genieError = {
        type: GenieErrorType.WARMING_UP,
        message: this.translate.instant('AI_CHAT.ERRORS.WAREHOUSE_WARMING_UP'),
        originalError: error,
      };
    } else if (error.status === 0) {
      // Status 0 indica problemas de red, CORS, o servidor no disponible
      genieError = {
        type: GenieErrorType.NETWORK,
        message: this.translate.instant('AI_CHAT.ERRORS.NETWORK_ERROR_MESSAGE'),
        originalError: error,
      };
    } else if (error.status === 401) {
      genieError = {
        type: GenieErrorType.AUTH_FAILED,
        message: serverMessage || this.translate.instant('AI_CHAT.ERRORS.AUTH_FAILED_GENERIC'),
        originalError: error,
      };
    } else if (error.status === 429) {
      genieError = {
        type: GenieErrorType.RATE_LIMIT,
        message: serverMessage || this.translate.instant('AI_CHAT.ERRORS.RATE_LIMIT_EXCEEDED'),
        originalError: error,
      };
      this.rateLimitTimeout = Date.now() + (this.getConfig().RATE_LIMIT_RESET || 60000);
    } else if (error.status === 500) {
      genieError = {
        type: GenieErrorType.UNKNOWN,
        message: serverMessage || error.message || this.translate.instant('AI_CHAT.ERRORS.INTERNAL_SERVER_ERROR'),
        originalError: error,
      };
    } else if (error.status === 502) {
      genieError = {
        type: GenieErrorType.NETWORK,
        message: serverMessage || this.translate.instant('AI_CHAT.ERRORS.BAD_GATEWAY'),
        originalError: error,
      };
    } else if (error.status === 503) {
      genieError = {
        type: GenieErrorType.NETWORK,
        message: serverMessage || this.translate.instant('AI_CHAT.ERRORS.SERVICE_UNAVAILABLE_TEMP'),
        originalError: error,
      };
    } else if (error.status === 504) {
      genieError = {
        type: GenieErrorType.TIMEOUT,
        message: serverMessage || this.translate.instant('AI_CHAT.ERRORS.GATEWAY_TIMEOUT'),
        originalError: error,
      };
    }

    // Ninguna rama aplica cuando el error no trae status HTTP (el Error plano de callGenieProxy):
    // ahí el mensaje del servidor es lo único informativo que tenemos, no lo tiramos.
    if (genieError.type === GenieErrorType.UNKNOWN && serverMessage) {
      genieError.message = serverMessage;
    }

    // Preservar información HTTP adicional en el error
    (genieError as any).status = error.status;
    (genieError as any).statusText = error.statusText;
    (genieError as any).url = error.url;

    // Preservar información del request (método y path)
    if (request) {
      (genieError as any).method = request.method;
      (genieError as any).relativePath = request.relativePath;
    }

    // Preservar el response completo del servidor para debugging
    if (error.error) {
      (genieError as any).serverResponse = error.error;
    }

    _error('LakehouseProxy API Error:', genieError);
    return throwError(() => genieError);
  }

  /** Inicia una nueva conversación con la IA */
  private startConversation(content: string): Observable<any> {
    if (!this.CAN_USE_DB_CHAT()) return of(null);

    // Si ya tenemos un conversation_id, lo devolvemos
    if (this.lasConversationResponseSubject.value) {
      return new Observable<any>(observer => {
        observer.next(this.lasConversationResponseSubject.value);
        observer.complete();
      });
    }

    const request: GenieProxyRequest = {
      method: 'POST',
      relativePath: 'spaces/{space_id}/start-conversation',
      body: { content },
    };

    return this.callGenieProxy(request).pipe(
      // Seguro de reintentar aunque sea un POST: en warm-up el 503 llega antes de que Databricks
      // cree la conversación, así que no se duplica nada.
      this.retryTransientDatabricksErrors('startConversation', GENIE_RETRY_MAX_TOTAL_WAIT_MS),
      map(response => {
        const conversationData = typeof response.content === 'string' ? JSON.parse(response.content) : response.content;
        if (conversationData?.conversation_id) {
          this.lasConversationResponseSubject.next(conversationData);
        } else {
          _error('Error starting conversation', conversationData);
        }
        return conversationData;
      })
    );
  }

  /** Obtiene el resultado de un mensaje específico */
  private getMessageResult(conversationId: string, messageId: string): Observable<any> {
    if (!this.CAN_USE_DB_CHAT()) return of(null);

    const request: GenieProxyRequest = {
      method: 'GET',
      relativePath: 'spaces/{space_id}/conversations/{conversation_id}/messages/{message_id}',
      variables: {
        conversation_id: conversationId,
        message_id: messageId,
      },
    };

    return this.callGenieProxy(request).pipe(
      map(response => {
        const messageData = typeof response.content === 'string' ? JSON.parse(response.content) : response.content;
        if (!messageData) _error('Error getting message result', messageData);
        return messageData;
      })
    );
  }

  /** Envía un mensaje en una conversación existente o crea una nueva */
  private sendMessage(conversationId: string | null, content: string): Observable<any> {
    if (!this.CAN_USE_DB_CHAT()) return of(null);

    // Si no tenemos conversación guardada o no se proporciona ID, crear una nueva
    if (!this.lasConversationResponseSubject.value?.conversation_id && !conversationId) {
      return this.startConversation(content).pipe(
        switchMap(response => {
          if (response?.conversation_id) {
            return this.sendMessage(response.conversation_id, content);
          }
          _error('Error creating conversation', response);
          return of(null);
        })
      );
    }

    const finalConversationId = conversationId || this.lasConversationResponseSubject.value?.conversation_id;

    const request: GenieProxyRequest = {
      method: 'POST',
      relativePath: 'spaces/{space_id}/conversations/{conversation_id}/messages',
      variables: {
        conversation_id: finalConversationId,
      },
      body: { content },
    };

    return this.callGenieProxy(request).pipe(
      // Mismo razonamiento que en startConversation: el 503 de warm-up llega antes de que el mensaje
      // se registre en la conversación.
      this.retryTransientDatabricksErrors('sendMessage', GENIE_RETRY_MAX_TOTAL_WAIT_MS),
      map(response => {
        const messageData = typeof response.content === 'string' ? JSON.parse(response.content) : response.content;
        if (!messageData?.id) {
          _error('Error sending message', messageData);
        }
        return messageData;
      })
    );
  }

  /**
   * Sistema de polling genérico para consultas que requieren espera
   * @param checkFn Función que realiza la consulta
   * @param predicate Función que evalúa si el resultado está completo
   * @param maxAttempts Número máximo de intentos
   */
  private pollGenieMessage<T>(
    checkFn: () => Observable<T>,
    isComplete: (result: T) => boolean,
    maxAttempts = this.getConfig().MAX_ATTEMPTS
  ): Observable<any> {
    return new Observable(observer => {
      let attempts = 0;

      const poll = () => {
        if (attempts >= maxAttempts) {
          // Error y no string: los handlers de abajo leen .message, y un string se pierde entero.
          observer.error(new Error(`Maximum attempts reached (${maxAttempts})`));
          return;
        }

        attempts++;

        checkFn()
          .pipe(
            timeout(this.getConfig().TIMEOUT),
            catchError(error => {
              if (attempts >= maxAttempts) {
                throw error;
              }
              return timer(this.getConfig().POLLING_FREQUENCY).pipe(switchMap(() => checkFn()));
            })
          )
          .subscribe({
            next: result => {
              if (isComplete(result)) {
                observer.next(result);
                observer.complete();
              } else {
                setTimeout(poll, this.getConfig().POLLING_FREQUENCY);
              }
            },
            error: error => observer.error(error),
          });
      };

      poll();
    }).pipe(
      retryWhen(errors =>
        errors.pipe(
          // El `retry(N)` que había acá NO acotaba nada: se aplica al notifier, que nunca falla, así
          // que cada error volvía a suscribir la fuente para siempre (medido: 134 resuscripciones en
          // 200ms). Y como `attempts` se declara dentro del subscribe, cada resuscripción lo reseteaba
          // a 0: el polling reintentaba de cero indefinidamente contra el proxy.
          // concatMap con índice sí acota, y espera a que termine el timer antes del error siguiente.
          concatMap((error, index) => {
            const retryAttempts = this.getConfig().RETRY_ATTEMPTS;
            if (index >= retryAttempts) {
              _error(`[pollGenieMessage] Sin más reintentos tras ${retryAttempts}`);
              return throwError(error);
            }

            // Si estamos rate-limiteados, esperar a que se libere en vez del delay fijo.
            const rateLimitWaitMs = this.rateLimitTimeout ? this.rateLimitTimeout - Date.now() : 0;
            const delayMs = rateLimitWaitMs > 0 ? rateLimitWaitMs : this.getConfig().RETRY_DELAY;

            _warn(`[pollGenieMessage] Reintento ${index + 1}/${retryAttempts} en ${delayMs}ms`);
            return timer(delayMs);
          })
        )
      )
    );
  }

  /** Maneja el flujo completo de envío y espera de respuesta de un mensaje */
  private chat(content: string, conversationId: string | null = null): Observable<any> {
    if (!this.CAN_USE_DB_CHAT()) return of(null);

    const startTime = Date.now();
    _log('[chat] INICIO - Enviando mensaje a Genie');

    return this.sendMessage(conversationId, content).pipe(
      switchMap(response => {
        if (!response?.message_id) return of(null);

        const messageId = response.message_id;
        const convId = response.conversation_id || this.lasConversationResponseSubject.value?.conversation_id;

        _log('[chat] Mensaje enviado, esperando respuesta (messageId:', messageId + ')');

        return this.pollGenieMessage(
          () => this.getMessageResult(convId, messageId),
          result => result?.status === 'COMPLETED' || result?.status === 'FAILED'
        ).pipe(
          tap(() => {
            const elapsed = Date.now() - startTime;
            _log('[chat] FIN - Tiempo total en chat:', `${elapsed}ms`);
          }),
          catchError(err => this.genericHandleError(err)),
          finalize(() => {})
        );
      })
    );
  }

  /**
   * Ejecuta una consulta SQL generada por la IA
   * @param conversationId ID de la conversación
   * @param messageId ID del mensaje que contiene la consulta
   * @param attachmentId ID del attachment que contiene la consulta
   */
  private executeQuery(conversationId: string, messageId: string, attachmentId: string): Observable<GenieStatementResponse> {
    if (!this.CAN_USE_DB_CHAT()) return of(null);

    const startTime = Date.now();
    _log('[executeQuery] INICIO - Obteniendo resultado de query ejecutada');

    const request: GenieProxyRequest = {
      method: 'GET',
      relativePath: 'spaces/{space_id}/conversations/{conversation_id}/messages/{message_id}/query-result/{attachment_id}',
      variables: {
        conversation_id: conversationId,
        message_id: messageId,
        attachment_id: attachmentId,
      },
    };

    return this.callGenieProxy<any>(request).pipe(
      // GET idempotente: se pueden reintentar también los 502/504 del proxy. Sin esto, un gateway
      // transitorio hace que sendGenieMessage degrade a solo-texto y el usuario pierde la tabla.
      this.retryTransientDatabricksErrors('executeQuery', GENIE_RETRY_MAX_TOTAL_WAIT_MS, true),
      tap(() => {
        const elapsed = Date.now() - startTime;
        _log('[executeQuery] FIN - Tiempo total para obtener resultado:', `${elapsed}ms`);
      }),
      map(response => {
        const queryData = typeof response.content === 'string' ? JSON.parse(response.content) : response.content;

        // Verificar si el response está vacío (caso común cuando Databricks devuelve solo texto)
        if (!queryData || Object.keys(queryData).length === 0) {
          _warn('[executeQuery] Query result endpoint devolvió objeto vacío - los datos no están disponibles de forma estructurada');
          throw new Error('Query result not available - data is in text format only');
        }

        const formattedResponse = _cloneDeep(queryData?.statement_response || queryData || {});

        const statementResponse: GenieStatementResponse = {
          statement_response: {
            ...formattedResponse,
            result: {
              ...formattedResponse.result,
              data_typed_array:
                formattedResponse.result?.data_typed_array ||
                formattedResponse.result?.data_array?.map(row => ({
                  values: row.map(value => {
                    // Determine the type of value and create the corresponding structure
                    if (typeof value === 'string') {
                      return { str: value };
                    } else if (typeof value === 'number') {
                      return { num: value };
                    } else if (typeof value === 'boolean') {
                      return { bool: value };
                    } else if (value === null) {
                      return {}; // Represent null values as empty objects
                    } else {
                      return { str: String(value) }; // Default to string for other types
                    }
                  }),
                })) ||
                [],
            },
          },
        };

        _log('executeQuery - formatted response', {
          original: queryData,
          formatted: statementResponse,
        });

        return statementResponse;
      }),
      catchError(error => {
        _error('Error executing query', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Método público principal para interactuar con la IA
   * Maneja tanto respuestas textuales como consultas SQL
   * @param content Mensaje para la IA
   * @param conversationId ID de conversación opcional
   * @param index Índice del attachment a procesar
   */
  public sendGenieMessage(content: string, conversationId?: string, index = 0): Observable<DataBricksAiGenieChatResponse> {
    if (!this.CAN_USE_DB_CHAT()) return of({ error: GenieErrorType.DISABLED });

    if (this.rateLimitTimeout && Date.now() < this.rateLimitTimeout) {
      const secondsLeft = Math.ceil((this.rateLimitTimeout - Date.now()) / 1000);
      return throwError(() => ({
        type: GenieErrorType.RATE_LIMIT,
        message: `${this.translate.instant('AI_CHAT.ERRORS.RATE_LIMIT_EXCEEDED')}. ${secondsLeft}s`,
      }));
    }

    const startTime = Date.now();
    const logMessage = `Query iniciada: "${content.substring(0, 100)}${content.length > 100 ? '...' : ''}"`;
    _log('[sendGenieMessage] INICIO -', logMessage);

    return this.chat(content, conversationId).pipe(
      switchMap(response => {
        const attachment = response?.attachments?.[index];
        if (!attachment) {
          return of({ text: this.translate.instant('AI_CHAT.ERRORS.PROCESSING_ERROR') });
        }

        const baseResponse = {
          text: attachment.text?.content || attachment.query?.description,
          rawResponse: response as GenieMessage,
        };

        // Estrategia 1: Buscar attachment con query.query (formato tradicional)
        let queryAttachment = response?.attachments?.find(att => att.query?.query && att.attachment_id);

        // Estrategia 2: Si el content del mensaje es SQL, intentar obtener resultado del primer attachment con ID
        // Esto ocurre cuando Databricks devuelve la query en el content pero los datos en un attachment
        if (!queryAttachment && response.content && response.attachments?.[0]?.attachment_id) {
          // Verificar si el content parece ser SQL
          const isSQLContent = /SELECT|FROM|WHERE|INSERT|UPDATE|DELETE/i.test(response.content);
          if (isSQLContent) {
            _log('[sendGenieMessage] Detectado SQL en content, intentando obtener resultado del primer attachment');
            queryAttachment = response.attachments[0];
          }
        }

        return queryAttachment
          ? this.executeQuery(response.conversation_id, response.message_id, queryAttachment.attachment_id).pipe(
              map(queryResult => ({
                ...baseResponse,
                queryProcessResult: queryResult,
              })),
              catchError(queryError => {
                _warn('[sendGenieMessage] Error obteniendo query result, devolviendo solo texto:', queryError);
                return of(baseResponse);
              })
            )
          : of(baseResponse);
      }),
      catchError((error: GenieError | (() => GenieError)) => {
        // Si el error es una función (viene de throwError(() => error)), ejecutarla
        const actualError: GenieError = typeof error === 'function' ? error() : error;

        const elapsed = Date.now() - startTime;
        _error('Chat Text Error:', actualError);
        _log('[sendGenieMessage] ERROR - Tiempo total:', `${elapsed}ms`);

        return of({
          text: actualError.message || this.translate.instant('AI_CHAT.ERRORS.PROCESSING_ERROR'),
          error: actualError.type,
          originalError: actualError.originalError, // Preservar el HttpErrorResponse original
          status: (actualError as any).status, // Preservar status HTTP
          statusText: (actualError as any).statusText,
          url: (actualError as any).url,
          method: (actualError as any).method, // Preservar método HTTP
          relativePath: (actualError as any).relativePath, // Preservar path del endpoint
          serverResponse: (actualError as any).serverResponse, // Preservar response del servidor
        });
      }),
      tap((response: any) => {
        const elapsed = Date.now() - startTime;
        if (response?.queryProcessResult) {
          _log('[sendGenieMessage] FIN (con query SQL) - Tiempo total:', `${elapsed}ms`);
        } else if (response?.text && !response?.error) {
          _log('[sendGenieMessage] FIN (solo texto) - Tiempo total:', `${elapsed}ms`);
        }
      })
    );
  }

  public resetConversation(): void {
    this.lasConversationResponseSubject.next(null);
  }

  /** Método público para verificar el estado del servicio */
  public getServiceHealth(): Observable<any> {
    return this.checkHealth();
  }

  /**
   * Ejecuta una query SQL directamente usando la Statement Execution API de Databricks
   * (Sin pasar por Genie, más rápido y sin polling)
   *
   * Proceso:
   * 1. POST /api/2.0/sql/statements con warehouse_id y statement
   * 2. Si la query es rápida (<10s), devuelve resultado inline
   * 3. Si no, devuelve statement_id para polling con GET /api/2.0/sql/statements/{id}
   *
   * @param sqlQuery Query SQL a ejecutar
   * @param warehouseId ID del warehouse de Databricks (requerido)
   * @param catalog Optional catalog name
   * @param schema Optional schema name
   * @returns Observable con DatabricksStatementExecutionResponse
   */
  public executeStatementDirect(
    sqlQuery: string,
    warehouseId: string,
    catalog?: string,
    schema?: string
  ): Observable<DatabricksStatementExecutionResponse> {
    if (!this.CAN_USE_DB_CHAT()) {
      return throwError(() => ({ type: GenieErrorType.DISABLED, message: 'Lakehouse chat is disabled' }));
    }

    // VALIDACIÓN 1: warehouse_id requerido
    if (!warehouseId) {
      _error('[executeStatementDirect] warehouse_id is required');
      return throwError(() => ({ type: GenieErrorType.UNKNOWN, message: 'warehouse_id is required for Statement Execution API' }));
    }

    // VALIDACIÓN 2: Validar formato de warehouse_id (solo alfanuméricos y guiones)
    if (!/^[a-zA-Z0-9_-]+$/.test(warehouseId) || warehouseId.length > MAX_WAREHOUSE_ID_LENGTH) {
      _error('[executeStatementDirect] Invalid warehouse_id format:', warehouseId);
      return throwError(() => ({ type: GenieErrorType.UNKNOWN, message: 'Invalid warehouse_id format' }));
    }

    // VALIDACIÓN 3: SQL query no puede estar vacío
    if (!sqlQuery || typeof sqlQuery !== 'string' || sqlQuery.trim().length === 0) {
      _error('[executeStatementDirect] SQL query is empty or invalid');
      return throwError(() => ({ type: GenieErrorType.UNKNOWN, message: 'SQL query cannot be empty' }));
    }

    // VALIDACIÓN 4: Límite de tamaño de SQL query
    if (sqlQuery.length > MAX_SQL_QUERY_LENGTH) {
      _error('[executeStatementDirect] SQL query too large:', sqlQuery.length, 'bytes');
      return throwError(() => ({
        type: GenieErrorType.UNKNOWN,
        message: `SQL query exceeds maximum length of ${MAX_SQL_QUERY_LENGTH} characters`,
      }));
    }

    // VALIDACIÓN 5: Detectar comandos SQL peligrosos (DROP, TRUNCATE, etc.)
    const dangerousPatterns = /\b(DROP|TRUNCATE|ALTER\s+TABLE|GRANT|REVOKE)\b/i;
    if (dangerousPatterns.test(sqlQuery)) {
      _warn('[executeStatementDirect] Potentially dangerous SQL command detected:', sqlQuery.substring(0, 100));
      // No bloqueamos, solo logueamos - puede ser legítimo en algunos casos
    }

    // VALIDACIÓN 6: Circuit breaker - si hay demasiados errores consecutivos, bloquear temporalmente
    if (this.circuitBreakerOpenUntil > Date.now()) {
      const secondsLeft = Math.ceil((this.circuitBreakerOpenUntil - Date.now()) / 1000);
      _error('[executeStatementDirect] Circuit breaker open, too many consecutive errors. Retry in', secondsLeft, 's');
      return throwError(() => ({
        type: GenieErrorType.UNKNOWN,
        message: `Too many errors. Service temporarily unavailable. Retry in ${secondsLeft}s`,
      }));
    }

    const requestBody: DatabricksStatementExecutionRequest = {
      warehouse_id: warehouseId,
      statement: sqlQuery,
      format: 'JSON_ARRAY',
      disposition: 'INLINE', // Intentar devolver inline si es rápido
      wait_timeout: '10s', // Esperar hasta 10 segundos
      on_wait_timeout: 'CONTINUE', // Si tarda más, devolver statement_id para polling
      catalog: catalog,
      schema: schema,
    };

    _log('[executeStatementDirect] Ejecutando SQL directo:', {
      queryLength: sqlQuery.length,
      queryPreview: sqlQuery.substring(0, 100),
      warehouseId,
      catalog,
      schema,
      timestamp: new Date().toISOString(),
    });

    // POST /api/2.0/sql/statements - Intentar salir del contexto /genie/ usando ../
    const request: GenieProxyRequest = {
      method: 'POST',
      relativePath: '../sql/statements',
      body: requestBody,
    };

    return this.callGenieProxy<DatabricksStatementExecutionResponse>(request).pipe(
      timeout(STATEMENT_EXECUTION_TIMEOUT_MS), // Timeout general para toda la operación
      map(proxyResponse => {
        // Validar que proxyResponse y content existan
        if (!proxyResponse || !proxyResponse.content) {
          _error('[executeStatementDirect] ProxyResponse sin content:', proxyResponse);
          throw new Error('LakehouseProxy devolvió respuesta sin content');
        }
        // GenieProxy devuelve content como string JSON, parsearlo
        const parsedContent = typeof proxyResponse.content === 'string' ? JSON.parse(proxyResponse.content) : proxyResponse.content;
        return parsedContent;
      }),
      switchMap((statementResponse: DatabricksStatementExecutionResponse) => {
        _log('[executeStatementDirect] Respuesta inicial:', statementResponse);

        // Validar estructura básica de la respuesta
        if (!statementResponse || !statementResponse.status) {
          _error('[executeStatementDirect] Respuesta inválida sin estructura status:', statementResponse);
          throw new Error('Lakehouse devolvió respuesta con estructura inválida');
        }

        const state = statementResponse.status.state;

        // Si ya está completado, devolverlo (incluso si result está vacío - tabla sin datos)
        // Normalizamos result para que siempre tenga data_array
        if (state === 'SUCCEEDED') {
          _log('[executeStatementDirect] Query completada inline (rápida)');

          // Reset circuit breaker en caso de éxito
          this.consecutivePollingErrors = 0;

          // Si result es un objeto vacío {}, normalizarlo a estructura esperada
          if (statementResponse.result && !statementResponse.result.data_array) {
            statementResponse.result.data_array = [];
          }
          return of(statementResponse);
        }

        // Si está en progreso, hacer polling
        if (state === 'PENDING' || state === 'RUNNING') {
          _log('[executeStatementDirect] Query en progreso, iniciando polling...', statementResponse.statement_id);
          return this.pollStatementStatus(statementResponse.statement_id);
        }

        // Si falló, lanzar error con mensaje descriptivo
        if (state === 'FAILED') {
          const errorMsg = statementResponse.status.error?.message || 'Statement execution failed';
          _error('[executeStatementDirect] Query falló:', errorMsg);
          throw new Error(`Databricks Statement Execution failed: ${errorMsg}`);
        }

        // Caso inesperado
        _warn('[executeStatementDirect] Estado inesperado:', state);
        return of(statementResponse);
      }),
      // Reintenta warm-up del warehouse y concurrencia Delta. Va después del switchMap para que un
      // 503 durante el polling también reintente desde el POST inicial: si el warehouse se cayó a
      // mitad de vuelo, el statement quedó abortado y hay que volver a mandarlo.
      this.retryTransientDatabricksErrors('executeStatementDirect'),
      catchError(error => {
        // Incrementar contador de errores consecutivos
        this.consecutivePollingErrors++;

        // Logging detallado del error
        const errorDetails = {
          message: error?.message || 'Unknown error',
          type: error?.type || error?.name,
          consecutiveErrors: this.consecutivePollingErrors,
          timestamp: new Date().toISOString(),
          warehouseId,
          queryLength: sqlQuery.length,
        };
        _error('[executeStatementDirect] Error ejecutando statement:', errorDetails);

        // Activar circuit breaker si hay demasiados errores consecutivos
        if (this.consecutivePollingErrors >= MAX_CONSECUTIVE_ERRORS) {
          this.circuitBreakerOpenUntil = Date.now() + CIRCUIT_BREAKER_RESET_MS;
          _error('[executeStatementDirect] Circuit breaker activated. Too many consecutive errors:', this.consecutivePollingErrors);
        }

        // Detectar específicamente el problema de GenieProxy
        if (error?.message?.includes('/2.0/genie/') || error?.message?.includes('ENDPOINT_NOT_FOUND')) {
          const enhancedError = new Error(
            'LakehouseProxy no soporta Statement Execution API. El backend está configurado solo para Genie API. ' +
              'Se requiere modificar LakehouseProxy o crear un endpoint separado para Statement Execution API.'
          );
          enhancedError['originalError'] = error;
          return throwError(() => enhancedError);
        }

        // Detectar timeout
        if (error?.name === 'TimeoutError') {
          return throwError(() => new Error(`Statement execution timeout after ${STATEMENT_EXECUTION_TIMEOUT_MS}ms`));
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Hace polling del estado de un statement hasta que complete o falle
   * @param statementId ID del statement a consultar
   * @returns Observable con el resultado final
   */
  private pollStatementStatus(statementId: string): Observable<DatabricksStatementExecutionResponse> {
    // VALIDACIÓN 1: statementId requerido
    if (!statementId || typeof statementId !== 'string') {
      _error('[pollStatementStatus] Invalid statementId:', statementId);
      return throwError(() => new Error('Statement ID is required for polling'));
    }

    // VALIDACIÓN 2: Validar formato de statementId (solo alfanuméricos, guiones y UUIDs)
    if (!/^[a-zA-Z0-9_-]+$/.test(statementId) || statementId.length > MAX_STATEMENT_ID_LENGTH) {
      _error('[pollStatementStatus] Invalid statementId format:', statementId);
      return throwError(() => new Error('Invalid statement ID format'));
    }

    // VALIDACIÓN 3: Circuit breaker
    if (this.circuitBreakerOpenUntil > Date.now()) {
      const secondsLeft = Math.ceil((this.circuitBreakerOpenUntil - Date.now()) / 1000);
      _error('[pollStatementStatus] Circuit breaker open. Retry in', secondsLeft, 's');
      return throwError(() => new Error(`Service temporarily unavailable. Retry in ${secondsLeft}s`));
    }

    const config = this.getConfig();
    // Usar límite más estricto
    const maxAttempts = Math.min(config.MAX_ATTEMPTS || 30, MAX_POLLING_ATTEMPTS);
    const pollingFrequency = config.POLLING_FREQUENCY || 5000;

    let attempts = 0;
    let consecutiveErrors = 0;

    const poll = (): Observable<DatabricksStatementExecutionResponse> => {
      attempts++;

      // PROTECCIÓN 1: Límite de intentos
      if (attempts > maxAttempts) {
        _error('[pollStatementStatus] Max attempts reached:', attempts);
        this.consecutivePollingErrors++;
        return throwError(
          () => new Error(`Statement polling timeout after ${maxAttempts} attempts (${(maxAttempts * pollingFrequency) / 1000}s)`)
        );
      }

      // PROTECCIÓN 2: Detectar demasiados errores consecutivos en este polling
      if (consecutiveErrors >= 3) {
        _error('[pollStatementStatus] Too many consecutive errors in polling:', consecutiveErrors);
        return throwError(() => new Error('Polling aborted due to consecutive errors'));
      }

      const request: GenieProxyRequest = {
        method: 'GET',
        relativePath: `../sql/statements/${statementId}`,
      };

      return this.callGenieProxy<DatabricksStatementExecutionResponse>(request).pipe(
        map(proxyResponse => {
          // GenieProxy devuelve content como string JSON, parsearlo
          const parsedContent = typeof proxyResponse.content === 'string' ? JSON.parse(proxyResponse.content) : proxyResponse.content;
          return parsedContent;
        }),
        switchMap((statementResponse: DatabricksStatementExecutionResponse) => {
          // VALIDACIÓN: Verificar estructura de respuesta
          if (!statementResponse || !statementResponse.status || !statementResponse.status.state) {
            _error('[pollStatementStatus] Invalid response structure:', statementResponse);
            consecutiveErrors++;
            throw new Error('Invalid statement response structure');
          }

          const state = statementResponse.status.state;

          _log(`[pollStatementStatus] Intento ${attempts}/${maxAttempts} - Estado: ${state}`, {
            statementId,
            consecutiveErrors,
            timestamp: new Date().toISOString(),
          });

          if (state === 'SUCCEEDED') {
            _log('[pollStatementStatus] Statement completado exitosamente');

            // Reset circuit breaker y contadores en caso de éxito
            this.consecutivePollingErrors = 0;
            consecutiveErrors = 0;

            // Normalizar result para que siempre tenga data_array
            if (statementResponse.result && !statementResponse.result.data_array) {
              statementResponse.result.data_array = [];
            }
            return of(statementResponse);
          }

          if (state === 'FAILED' || state === 'CANCELED') {
            const errorMsg = statementResponse.status.error?.message || `Statement ${state.toLowerCase()}`;
            const errorDetails = {
              state,
              message: errorMsg,
              statementId,
              attempts,
              timestamp: new Date().toISOString(),
            };
            _error('[pollStatementStatus] Statement falló:', errorDetails);

            // Incrementar contador de errores
            this.consecutivePollingErrors++;
            consecutiveErrors++;

            throw new Error(`Statement execution ${state.toLowerCase()}: ${errorMsg}`);
          }

          // Reset consecutive errors si estamos progresando
          if (state === 'RUNNING' || state === 'PENDING') {
            consecutiveErrors = 0;
          }

          // Continuar polling con mejor manejo de memory leaks
          return timer(pollingFrequency).pipe(
            take(1), // Asegurar que solo emita una vez
            switchMap(() => poll())
          );
        }),
        catchError(err => {
          _error('[pollStatementStatus] Error in poll iteration:', {
            error: err?.message,
            attempt: attempts,
            consecutiveErrors,
            statementId,
          });
          consecutiveErrors++;
          this.consecutivePollingErrors++;
          throw err;
        })
      );
    };

    return poll();
  }

  /**
   * Ejecuta un statement SQL directo y descarga el resultado completo como CSV
   * Usa disposition INLINE para obtener todos los datos y los convierte a CSV en el frontend
   * @param sqlQuery Query SQL a ejecutar
   * @param warehouseId ID del warehouse
   * @param catalog Catalog name
   * @param schema Schema name
   * @returns Observable con los datos completos para convertir a CSV
   */
  public executeStatementDirectCSVDownload(
    sqlQuery: string,
    warehouseId: string,
    catalog?: string,
    schema?: string
  ): Observable<DatabricksStatementExecutionResponse> {
    if (!this.CAN_USE_DB_CHAT()) {
      return throwError(() => ({ type: GenieErrorType.DISABLED, message: 'Databricks chat is disabled' }));
    }

    // VALIDACIÓN 1: warehouse_id requerido
    if (!warehouseId) {
      _error('[executeStatementDirectCSVDownload] warehouse_id is required');
      return throwError(() => ({ type: GenieErrorType.UNKNOWN, message: 'warehouse_id is required for Statement Execution API' }));
    }

    // VALIDACIÓN 2: Validar formato de warehouse_id
    if (!/^[a-zA-Z0-9_-]+$/.test(warehouseId) || warehouseId.length > MAX_WAREHOUSE_ID_LENGTH) {
      _error('[executeStatementDirectCSVDownload] Invalid warehouse_id format:', warehouseId);
      return throwError(() => ({ type: GenieErrorType.UNKNOWN, message: 'Invalid warehouse_id format' }));
    }

    // VALIDACIÓN 3: SQL query no puede estar vacío
    if (!sqlQuery || typeof sqlQuery !== 'string' || sqlQuery.trim().length === 0) {
      _error('[executeStatementDirectCSVDownload] SQL query is empty or invalid');
      return throwError(() => ({ type: GenieErrorType.UNKNOWN, message: 'SQL query cannot be empty' }));
    }

    // VALIDACIÓN 4: Límite de tamaño de SQL query
    if (sqlQuery.length > MAX_SQL_QUERY_LENGTH) {
      _error('[executeStatementDirectCSVDownload] SQL query too large:', sqlQuery.length, 'bytes');
      return throwError(() => ({
        type: GenieErrorType.UNKNOWN,
        message: `SQL query exceeds maximum length of ${MAX_SQL_QUERY_LENGTH} characters`,
      }));
    }

    const requestBody: DatabricksStatementExecutionRequest = {
      warehouse_id: warehouseId,
      statement: sqlQuery,
      format: 'JSON_ARRAY',
      disposition: 'INLINE', // Usamos INLINE para obtener todos los datos
      wait_timeout: '30s', // Esperar hasta 30 segundos
      on_wait_timeout: 'CONTINUE', // Si tarda más, hacer polling
      catalog: catalog,
      schema: schema,
    };

    _log('[executeStatementDirectCSVDownload] Solicitando datos completos para CSV:', {
      queryLength: sqlQuery.length,
      queryPreview: sqlQuery.substring(0, 100),
      warehouseId,
      catalog,
      schema,
      timestamp: new Date().toISOString(),
    });

    const request: GenieProxyRequest = {
      method: 'POST',
      relativePath: '../sql/statements',
      body: requestBody,
    };

    return this.callGenieProxy<DatabricksStatementExecutionResponse>(request).pipe(
      timeout(STATEMENT_EXECUTION_TIMEOUT_MS),
      map(proxyResponse => {
        // Validar que proxyResponse y content existan
        if (!proxyResponse || !proxyResponse.content) {
          _error('[executeStatementDirectCSVDownload] ProxyResponse sin content:', proxyResponse);
          throw new Error('GenieProxy devolvió respuesta sin content');
        }

        // GenieProxy devuelve content como string JSON, parsearlo
        const parsedContent = typeof proxyResponse.content === 'string' ? JSON.parse(proxyResponse.content) : proxyResponse.content;

        return parsedContent;
      }),
      switchMap((statementResponse: DatabricksStatementExecutionResponse) => {
        _log('[executeStatementDirectCSVDownload] Respuesta inicial:', statementResponse);

        // Validar estructura básica de la respuesta
        if (!statementResponse || !statementResponse.status) {
          _error('[executeStatementDirectCSVDownload] Respuesta inválida sin estructura status:', statementResponse);
          throw new Error('Databricks devolvió respuesta con estructura inválida');
        }

        const state = statementResponse.status.state;

        // Si ya está completado, devolverlo
        if (state === 'SUCCEEDED') {
          _log('[executeStatementDirectCSVDownload] Query completada, datos listos para CSV');

          // Normalizar result para que siempre tenga data_array
          if (statementResponse.result && !statementResponse.result.data_array) {
            statementResponse.result.data_array = [];
          }
          return of(statementResponse);
        }

        // Si está en progreso, hacer polling
        if (state === 'PENDING' || state === 'RUNNING') {
          _log('[executeStatementDirectCSVDownload] Query en progreso, iniciando polling...', statementResponse.statement_id);
          return this.pollStatementStatus(statementResponse.statement_id);
        }

        // Si falló, lanzar error
        if (state === 'FAILED') {
          const errorMsg = statementResponse.status.error?.message || 'Statement execution failed';
          _error('[executeStatementDirectCSVDownload] Query falló:', errorMsg);
          throw new Error(`Databricks Statement Execution failed: ${errorMsg}`);
        }

        // Caso inesperado
        _warn('[executeStatementDirectCSVDownload] Estado inesperado:', state);
        return of(statementResponse);
      }),
      this.retryTransientDatabricksErrors('executeStatementDirectCSVDownload'),
      catchError(error => {
        _error('[executeStatementDirectCSVDownload] Error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Ejecuta un statement SQL directo y solicita el resultado como EXTERNAL_LINKS en formato CSV
   * Útil para descargar grandes volúmenes de datos de forma eficiente
   * @param sqlQuery Query SQL a ejecutar
   * @param warehouseId ID del warehouse
   * @param catalog Catalog name
   * @param schema Schema name
   * @returns Observable con la respuesta que contiene los links externos
   */
  public executeStatementDirectExternalCSV(
    sqlQuery: string,
    warehouseId: string,
    catalog?: string,
    schema?: string
  ): Observable<DatabricksStatementExecutionResponse> {
    if (!this.CAN_USE_DB_CHAT()) {
      return throwError(() => ({ type: GenieErrorType.DISABLED, message: 'Databricks chat is disabled' }));
    }

    const requestBody: DatabricksStatementExecutionRequest = {
      warehouse_id: warehouseId,
      statement: sqlQuery,
      format: 'CSV',
      disposition: 'EXTERNAL_LINKS',
      wait_timeout: '30s',
      on_wait_timeout: 'CONTINUE',
      catalog: catalog,
      schema: schema,
    };

    _log('[executeStatementDirectExternalCSV] Solicitando EXTERNAL_LINKS para CSV:', {
      queryPreview: sqlQuery.substring(0, 100),
      warehouseId,
    });

    const request: GenieProxyRequest = {
      method: 'POST',
      relativePath: '../sql/statements',
      body: requestBody,
    };

    return this.callGenieProxy<DatabricksStatementExecutionResponse>(request).pipe(
      map(proxyResponse => {
        const parsedContent = typeof proxyResponse.content === 'string' ? JSON.parse(proxyResponse.content) : proxyResponse.content;
        return parsedContent;
      }),
      switchMap((statementResponse: DatabricksStatementExecutionResponse) => {
        const state = statementResponse.status.state;

        if (state === 'SUCCEEDED') {
          return of(statementResponse);
        }

        if (state === 'PENDING' || state === 'RUNNING') {
          return this.pollStatementStatus(statementResponse.statement_id);
        }

        if (state === 'FAILED') {
          throw new Error(`Databricks Statement Execution failed: ${statementResponse.status.error?.message}`);
        }

        return of(statementResponse);
      }),
      this.retryTransientDatabricksErrors('executeStatementDirectExternalCSV')
    );
  }
}
